import { z } from 'zod';
/**
 * Gắn cập nhật tiến độ với commit — APM-PCOM, docs/plan-progress-commits.md.
 *
 * Dự án bật công tắc ⇒ tick một mục checklist / hoàn thành một việc mở hộp chọn
 * **nhánh → nhiều commit**; lựa chọn (hoặc "Không có commit") thành một **bản báo**
 * thuộc aggregate `task` (`task.progress_commits_reported`). Tệp riêng (không nối
 * vào `git.ts`) như các tệp `git-*.ts` khác.
 */
/** Thao tác tiến độ mở hộp chọn (§4.3). Kéo % chưa tới 100 không hỏi (Q1). */
export const GitProgressTrigger = z.enum(['checklist_item', 'completed']);
/** Trần commit trong một bản báo. */
export const GIT_PROGRESS_COMMITS_MAX = 50;
/** Số commit mới nhất đọc của một nhánh — cũng là tầm POST kiểm sha (§3). */
export const GIT_PROGRESS_COMMITS_PAGE = 100;
// ── Công tắc (§2.1) ─────────────────────────────────────────────────────────
/** Vì sao chưa bật được: chưa có kho đang gắn · có kho nhưng token / máy chủ không dùng được. */
export const GitProgressUnavailableReason = z.enum(['no_repository', 'connection_unusable']);
/**
 * `GET /projects/:projectId/git/progress-commits/status` (thành viên dự án).
 *
 * `effective` = hộp chọn có mở cho **người gọi** không: công tắc bật, dự án có kho
 * dùng được, và người gọi có `git:read` (khách / khách hàng không bao giờ có).
 *
 * ⚠️ Không nhận asOf: cấu hình hiện hành, không phải đại lượng theo thời gian. Lịch
 * sử nằm ở event `git_settings.changed`.
 */
// ── AI rà commit đã báo — APM-PCRV (docs/plan-progress-commit-review.md) ──────
/** Tên skill — job gọi bằng tên này. */
export const REVIEW_PROGRESS_COMMITS_SKILL = 'review_progress_commits';
export const GitProgressReviewStatus = z.enum(['pending', 'done', 'failed', 'skipped']);
/** Ba mức (Q2): `partial` và `unrelated` là "thấp" ⇒ nhãn trên thẻ + báo PM. */
export const GitProgressReviewVerdict = z.enum(['related', 'partial', 'unrelated']);
export const GitProgressReviewBasis = z.enum(['diff', 'message']);
export function isLowProgressReview(verdict) {
    return verdict === 'partial' || verdict === 'unrelated';
}
/** Vì sao bật công tắc con chưa được. */
export const GitProgressReviewUnavailableReason = z.enum(['commits_off', 'ai_not_configured']);
/** Bằng chứng đã lưu — **không** có chữ nào của diff (Ngoại lệ 5 của luật #3). */
export const GitProgressReviewEvidence = z.object({
    repositoryId: z.uuid(),
    sha: z.string(),
    path: z.string().nullable(),
});
export const GitProgressReviewReadStats = z.object({
    files: z.number().int().nonnegative(),
    filesRead: z.number().int().nonnegative(),
    bytesRead: z.number().int().nonnegative(),
    narrowed: z.boolean(),
});
/** Output của skill `review_progress_commits` — `quote` chỉ để `finalize` kiểm, bỏ trước khi lưu. */
export const GitProgressReviewOutput = z.object({
    verdict: GitProgressReviewVerdict,
    summary: z.string().trim().min(1).max(300),
    evidence: z
        .array(z.object({
        repositoryId: z.string(),
        sha: z.string(),
        path: z.string().nullable(),
        quote: z.string().trim().min(1).max(300),
    }))
        .max(5),
    /** Đặt từ kết quả tool, không do model khai. */
    basedOn: GitProgressReviewBasis.optional(),
    readStats: GitProgressReviewReadStats.optional(),
});
/** Kết quả rà của một bản báo, kèm theo bản báo. */
export const GitProgressReview = z.object({
    status: GitProgressReviewStatus,
    verdict: GitProgressReviewVerdict.nullable(),
    basedOn: GitProgressReviewBasis.nullable(),
    summary: z.string().nullable(),
    evidence: z.array(GitProgressReviewEvidence),
    readStats: GitProgressReviewReadStats.nullable(),
    errorCode: z.string().nullable(),
    createdAt: z.iso.datetime(),
    finishedAt: z.iso.datetime().nullable(),
    acknowledgedBy: z.object({ id: z.uuid(), name: z.string() }).nullable(),
    acknowledgedAt: z.iso.datetime().nullable(),
    /** Người xem bấm "Đã xem" được (PM). */
    canAcknowledge: z.boolean(),
    /** Người xem bấm "Rà lại" được (`failed` / `skipped`, người báo hoặc PM). */
    canRetry: z.boolean(),
});
/** Việc có lượt rà thấp **chưa** "Đã xem" — thẻ việc đọc danh sách này. */
export const GitProgressReviewWarning = z.object({
    taskId: z.uuid(),
    /** Mức thấp nhất trong các lượt chưa xem. */
    verdict: GitProgressReviewVerdict,
    count: z.number().int().positive(),
    summary: z.string().nullable(),
});
export const GitProgressReviewWarnings = z.object({ warnings: z.array(GitProgressReviewWarning) });
/** Một lượt đang chạy ngầm của người xem — chữ (i) ở thanh trên. */
export const GitPendingProgressReview = z.object({
    reportId: z.uuid(),
    projectId: z.uuid(),
    taskId: z.uuid(),
    taskKey: z.string().nullable(),
    taskTitle: z.string(),
    trigger: GitProgressTrigger,
    checklistItemText: z.string().nullable(),
    createdAt: z.iso.datetime(),
});
export const GitPendingProgressReviews = z.object({
    pending: z.array(GitPendingProgressReview),
    /** Lượt vừa xong (≤ 10 phút) — web hiện toast khi một lượt rời `pending`. */
    recent: z.array(GitPendingProgressReview.extend({
        status: GitProgressReviewStatus,
        verdict: GitProgressReviewVerdict.nullable(),
        finishedAt: z.iso.datetime().nullable(),
    })),
});
export const GitProgressCommitsStatus = z.object({
    enabled: z.boolean(),
    available: z.boolean(),
    reason: GitProgressUnavailableReason.nullable(),
    effective: z.boolean(),
    /** Công tắc con "AI rà commit" — APM-PCRV. */
    review: z
        .object({
        enabled: z.boolean(),
        available: z.boolean(),
        reason: GitProgressReviewUnavailableReason.nullable(),
    })
        .optional(),
    /** Người gọi đổi được công tắc (`git:manage`). */
    canManage: z.boolean(),
});
/** `PUT /projects/:projectId/git/progress-commits` (`git:manage`). */
export const SetGitProgressCommitsInput = z.object({
    enabled: z.boolean(),
    /** Công tắc con (APM-PCRV); vắng = giữ nguyên. Tắt cha thì con tắt theo. */
    reviewWithAi: z.boolean().optional(),
});
// ── Hộp chọn: nhánh → commit (§3, APM-PCOM-b) ───────────────────────────────
export const GitProgressBranch = z.object({
    repositoryId: z.uuid(),
    repositoryPath: z.string(),
    name: z.string(),
    isDefault: z.boolean(),
    lastCommitAt: z.iso.datetime().nullable(),
    /** Tên nhánh mang mã của việc đang báo — đứng đầu và được chọn sẵn. */
    matchesTask: z.boolean(),
});
/** `GET …/progress-commits/branches?taskId=` — nhánh mang mã việc trước, rồi mới nhất trước. */
export const GitProgressBranchList = z.object({ branches: z.array(GitProgressBranch) });
/**
 * Query của `GET …/progress-commits/commits`. `all` là chuỗi query: `'1'` = mọi
 * tác giả. Không `z.coerce.boolean()` — `'false'` thành `true`.
 */
export const GitProgressCommitsQuery = z.object({
    repositoryId: z.uuid(),
    branch: z.string().trim().min(1).max(255),
    all: z.enum(['0', '1']).optional(),
});
export const GitProgressCommitOption = z.object({
    sha: z.string(),
    shortSha: z.string(),
    title: z.string(),
    authorName: z.string().nullable(),
    committedAt: z.iso.datetime(),
    webUrl: z.string().nullable(),
    /**
     * Email tác giả thuộc người gọi (email tài khoản ∪ email gắn tay ở
     * `git_identity`). ⚠️ Email commit là chuỗi tự khai — chỉ để **lọc hiển thị**.
     */
    mine: z.boolean(),
});
export const GitProgressCommitList = z.object({
    /** Mới nhất trước. `all` vắng ⇒ chỉ `mine`. */
    commits: z.array(GitProgressCommitOption),
    /** Số email nhận ra là của người gọi — 0 ⇒ giao diện nhắc gắn email git. */
    myEmails: z.number().int().nonnegative(),
    /** Số commit của người khác bị ẩn (khi `all` vắng). */
    hiddenOthers: z.number().int().nonnegative(),
});
// ── Bản báo (§2.2) ──────────────────────────────────────────────────────────
/** `POST /tasks/:taskId/progress-commits` — server tự lấy tiêu đề / tác giả từ máy chủ git. */
export const ReportGitProgressCommitsInput = z
    .object({
    trigger: GitProgressTrigger,
    checklistItemId: z.uuid().nullable().optional(),
    skipped: z.boolean(),
    commits: z
        .array(z.object({
        repositoryId: z.uuid(),
        branch: z.string().trim().min(1).max(255),
        sha: z
            .string()
            .trim()
            .regex(/^[0-9a-fA-F]{7,64}$/),
    }))
        .max(GIT_PROGRESS_COMMITS_MAX),
})
    .superRefine((value, ctx) => {
    if (value.trigger === 'checklist_item' && !value.checklistItemId) {
        ctx.addIssue({ code: 'custom', path: ['checklistItemId'], message: 'Thiếu mục checklist' });
    }
    if (value.trigger === 'completed' && value.checklistItemId) {
        ctx.addIssue({
            code: 'custom',
            path: ['checklistItemId'],
            message: 'Báo hoàn thành không kèm mục checklist',
        });
    }
    if (value.skipped !== (value.commits.length === 0)) {
        ctx.addIssue({
            code: 'custom',
            path: ['commits'],
            message: value.skipped
                ? '"Không có commit" không kèm commit nào'
                : 'Chọn ít nhất một commit, hoặc báo "Không có commit"',
        });
    }
});
export const GitProgressReportedCommit = z.object({
    repositoryId: z.uuid(),
    /** `null` = kho đã gỡ khỏi dự án (hàng còn nhờ ảnh chụp). */
    repositoryPath: z.string().nullable(),
    branch: z.string(),
    sha: z.string(),
    shortSha: z.string(),
    title: z.string(),
    authorName: z.string().nullable(),
    committedAt: z.iso.datetime(),
    webUrl: z.string().nullable(),
});
export const GitProgressReport = z.object({
    id: z.uuid(),
    trigger: GitProgressTrigger,
    checklistItemId: z.string().nullable(),
    checklistItemText: z.string().nullable(),
    skipped: z.boolean(),
    createdBy: z.object({ id: z.uuid(), name: z.string() }),
    createdAt: z.iso.datetime(),
    /** Người gọi gỡ được commit của bản báo này (người báo, hoặc `git:manage`). */
    canRemove: z.boolean(),
    commits: z.array(GitProgressReportedCommit),
    /** Lượt AI rà (APM-PCRV); `null` = không rà (công tắc con tắt lúc báo, hoặc "Không có commit"). */
    review: GitProgressReview.nullable().optional(),
});
/**
 * `GET /tasks/:taskId/progress-commits` — mới nhất trước. Bọc trong object: "chưa
 * có" là `{ reports: [] }`, không phải body rỗng.
 *
 * ⚠️ Không nhận asOf: danh sách bản báo tự mang mốc `createdAt` của từng hàng, và
 * Replay dựng chúng từ `task.progress_commits_reported`.
 */
export const TaskGitProgressReports = z.object({ reports: z.array(GitProgressReport) });
/** `7` ký tự đầu, như GitLab / GitHub hiển thị. */
export function shortGitSha(sha) {
    return sha.slice(0, 7);
}
//# sourceMappingURL=git-progress-commits.js.map